import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import PlayerData from "../PlayerData";
import { GameScene } from "../scenes/GameScene";
import { EnvironmentData, WallEnvironmentData } from "./ChunkSpawner";
import { DestroyOffscreen } from "./DestroyOffscreen";
import { Player } from "./Player";
import { WallBomb } from "./WallBomb";

type Bit = { x: number, y: number };

export class Wall extends GameObject {

	scene: GameScene;
	static height = 5000;
	wallIndex: number;
	leftSideFG: GameObject;
	bitSize = 40;
	xCount: number;
	bits: (Bit | null)[] = [];
	wallData: number[]; // 0 = not destroyed. 1 = destroyed. 2 = center of destruction (where wallCircle image should be placed)
	startedToTouch = false;
	passedMidpoint = false;
	passed = false;
	crumbleSFX: any;
	wallEnvironmentData: WallEnvironmentData;

	constructor(scene: GameScene, wallIndex: number, x: number, y: number) {
		super(scene);
		this.wallIndex = wallIndex;
		this.setPosition(x, y);
		const wallData = this.wallEnvironmentData = GameConfig.get<EnvironmentData[]>("environments")[wallIndex].wall;
		this.setSize(wallData.width, Wall.height);
		this.y -= this.height / 2;

		this.crumbleSFX = this.scene.sound.add("crumbleWall", { loop: true, volume: 0 });
		this.crumbleSFX.play();

		this.wallData = this.loadWallData(PlayerData.Instance.saveData.wallData[wallIndex]);

		// const back = this.addImage("whiteSquare");
		// back.setDisplaySize(this.width, this.height);
		// back.tint = 0xAA8844;
		// const debugBitsContainer = scene.add.container();

		const top = -this.height / 2;
		const left = -this.width / 2;
		const size = this.bitSize;
		const xCount = this.xCount = this.width / size;
		const yCount = this.height / size;

		const wallTileSize = 508;
		for (let y = top; y < this.height / 2; y += wallTileSize) {
			for (let x = left; x < this.width / 2; x += wallTileSize) {
				const tile = this.addImage("wallTile");
				tile.setOrigin(0, 0);
				tile.setPosition(x, y);
				const overflowX = Math.max((tile.x + tile.width) - (this.width / 2), 0);
				const overflowY = Math.max((tile.y + tile.height) - (this.height / 2), 0);
				if (overflowX > 0 || overflowY > 0) {
					tile.setCrop(0, 0, tile.width - overflowX, tile.height - overflowY);
				}
			}
		}

		// holds a duplicated version of the left wall that sorts in front of the player when they crash through.
		this.leftSideFG = new GameObject(scene);
		this.leftSideFG.setPosition(this.x, this.y);
		scene.addObject(this.leftSideFG, "foreground");
		this.leftSideFG.addComponent(new DestroyOffscreen(this.leftSideFG, true, false, 1000));
		this.leftSideFG.visible = false;

		for (let y = this.height / 2; y > -this.height / 2; y -= 1015) {
			const leftOffset = 200;

			for (const parent of [this, this.leftSideFG]) {
				const leftPiece = parent.addImage(y == this.height / 2 ? "wallStartBottom" : "wallStartTile");
				leftPiece.setOrigin(1, 1);
				leftPiece.setPosition(left + leftOffset, y);
			}

			const rightPiece = this.addImage("wallEnd");
			rightPiece.setOrigin(0, 1);
			rightPiece.setPosition(left + this.width - 46, y);
		}

		let i = 0;
		for (let yIndex = 0; yIndex < yCount; yIndex++) {
			for (let xIndex = 0; xIndex < xCount; xIndex++) {
				const x = left + (xIndex * size) + size / 2;
				const y = top + (yIndex * size) + size / 2;

				const num = this.wallData[i];
				if (num == null || num == 0) {
					this.bits[i] = { x: x, y: y };
					this.wallData[i] = 0;

					// const bit = this.addImage("whiteSquare");
					// debugBitsContainer.add(bit);
					// bit.setPosition(x, y);
					// bit.setSize(size, size);
					// bit.setDisplaySize(size, size);
					// bit.alpha = 0.5;
				}
				else {
					this.wallData[i] = num;
					if (num == 2) this.spawnDestructionCircle(x, y);
				}
				
				i++;
			}
		}

		const bombPositions = GameConfig.get("wallTntPositions") as { x: number, y: number }[];
		for (const pos of bombPositions) {
			const bombPos = { x: left + pos.x, y: top + pos.y };
			const globalBombPos = { x: this.x + bombPos.x, y: this.y + bombPos.y };
			const bomb = scene.addObject(new WallBomb(scene, globalBombPos.x, globalBombPos.y), "player");
			bomb.once(WallBomb.ON_DESTROYED, () => this.destroyBomb(bomb));

			const [gridX, gridY] = this.getGridPos(bomb);
			const size = 10;
			const startX = gridX - size / 2;
			const startY = gridY - size / 2;

			for (let x = startX; x < startX + size; x++) {
				for (let y = startY; y < startY + size; y++) {
					const i = this.getBitIndex(x, y);
					if (i == null) continue;
					const bit = this.bits[i];
					if (!bit) continue;
					const dist = Phaser.Math.Distance.Between(bit.x, bit.y, bombPos.x, bombPos.y);
					const isCenter = dist < 40;
					if (dist < 130) this.destroyBit(i, isCenter);
					if (isCenter) this.spawnDestructionCircle(bit.x, bit.y);
				}
			}
		}

		this.addComponent(new DestroyOffscreen(this, true, false, 1000));

		// this.add(debugBitsContainer);
	}

	destroyBomb(bomb: WallBomb): void {
		// create destruction circles in a radius around the bomb
		let dist = 175;
		let count = 6;
		for (let i = 0; i < count; i++) {
			const angle = (i / count) * (Math.PI * 2);
			const x = bomb.x + Math.cos(angle) * dist;
			const y = bomb.y + Math.sin(angle) * dist;
			const [gridX, gridY] = this.getGridPos({ x: x, y: y });
			const index = this.getBitIndex(gridX, gridY);
			if (index == null) continue;
			const bit = this.bits[index];
			if (bit == null) continue;
			this.destroyAroundBit(bit);
		}
	}

	destroy(): void {
		this.saveWallData(this.wallData);
		this.crumbleSFX.destroy();
		super.destroy();
	}

	loadWallData(str: string): number[] {
		const data: number[] = [];
		for (let i = 0; i < str.length; i++) {
			data[i] = Number.parseInt(str.charAt(i));
		}
		return data;
	}

	saveWallData(data: number[]): void {
		let str = "";
		for (let i = 0; i < data.length; i++) {
			str = str.concat(data[i].toString());
		}
		PlayerData.Instance.saveData.wallData[this.wallIndex] = str;
	}

	update(dt: number): void {
		this.crumbleSFX.volume -= (dt / 0.5);
		if (this.crumbleSFX.volume < 0) this.crumbleSFX.volume = 0;

		const player = this.scene.objects.find(o => o instanceof Player) as Player;
		player.parentContainer.bringToTop(player);

		if (!this.startedToTouch && player.x + player.width / 2 > this.x - this.width / 2) {
			const passed = player.testWallEnterExit(this.x - this.width / 2, this.wallEnvironmentData.minVelocityToEnterWall, true);
			if (passed) {
				this.startedToTouch = true;
				player.onEnteredWall();
				const hole = this.leftSideFG.addImage("wallFrontHole");
				const [x, y] = this.getLocalPos(player);
				hole.setPosition(x, y);
				this.leftSideFG.visible = true;
				this.crumbleAnim(player.x, player.y);
			}
		}

		if (this.startedToTouch) this.collidePlayer(player);
		
		const cameraCenter = this.scene.cameras.main.worldView.centerX;
		if (!this.passedMidpoint && cameraCenter > this.x) {
			this.passedMidpoint = true;
			this.scene.ltGame.onPassedWallMidpoint();
		}

		if (!this.passed && player.x > this.x + this.width / 2) {
			const passed = player.testWallEnterExit(this.x + this.width / 2, this.wallEnvironmentData.minVelocityToExitWall, false);
			if (passed) {
				this.passed = true;
				player.onPassedWall();
			}
		}
		super.update(dt);
	}

	collidePlayer(player: Player): void {
		// query bits near player to see if they're collided with
		const [gridX, gridY] = this.getGridPos(player);
		const [playerX, playerY] = this.getLocalPos(player);
		const playerRect = new Phaser.Geom.Rectangle(playerX - player.width / 2, playerY - player.height / 2, player.width, player.height);

		const startX = gridX - 2;
		const startY = gridY - 2;
		const intersectingBits: Bit[] = [];
		for (let x = startX; x < startX + 4; x++) {
			for (let y = startY; y < startY + 4; y++) {
				const index = this.getBitIndex(x, y);
				if (index == null) continue;
				const bit = this.bits[index];

				if (bit) {
					const bitRect = new Phaser.Geom.Rectangle(bit.x - this.bitSize / 2, bit.y - this.bitSize / 2, this.bitSize, this.bitSize);
					const intersects = Phaser.Geom.Intersects.RectangleToRectangle(playerRect, bitRect);
					if (intersects) intersectingBits.push(bit);
				}
			}
		}

		// find the closest bit the player's collided with and center the destruction circle on it
		if (intersectingBits.length > 0) {
			type BitDist = { bit?: Bit, dist: number };
			const closestBit = intersectingBits.reduce<BitDist>((a, b) => {
				const dist = Phaser.Math.Distance.Between(b.x, b.y, playerX, playerY);
				if (a.dist < dist) return a;
				else return { bit: b, dist: dist };
			}, { bit: null, dist: Phaser.Math.MAX_SAFE_INTEGER }).bit;
			
			this.hitWallAtPosition(player, closestBit);
		}
	}

	hitWallAtPosition(player: Player, centerBit: Bit) {
		const shouldDestroy = player.hitWallBit(this.wallEnvironmentData.minVelocityToBreakThroughWall);
		if (!shouldDestroy) { // player was stopped
			this.saveWallData(this.wallData);
			return;
		}

		this.destroyAroundBit(centerBit);
	}

	destroyAroundBit(centerBit: Bit) {
		// query bits near x & y
		const [gridX, gridY] = this.getLocalGridPos({ x: centerBit.x, y: centerBit.y });
		if (!centerBit) {
			return;
		}
		const startX = gridX - 3;
		const startY = gridY - 3;
		for (let x = startX; x < startX + 6; x++) {
			for (let y = startY; y < startY + 6; y++) {
				const index = this.getBitIndex(x, y);
				if (index == null) continue;
				const bit = this.bits[index];
				if (!bit) continue;
				
				// destroy bits within a 125px radius
				const dist = Phaser.Math.Distance.Between(centerBit.x, centerBit.y, bit.x, bit.y);
				const isCenter = (bit == centerBit);
				if (dist <= 130) this.destroyBit(index, isCenter);
			}
		}

		this.crumbleSFX.volume = 1;
		const [globalX, globalY] = this.getGlobalPos(centerBit);
		this.crumbleAnim(globalX, globalY);
		this.spawnDestructionCircle(centerBit.x, centerBit.y);
	}

	crumbleAnim(x: number, y: number): void {
		const emitter = new ParticleEmitter(this.scene);
		emitter.setPosition(x, y);
		this.scene.addObject(emitter, "foreground");
	}

	spawnDestructionCircle(x: number, y: number): void {
		const i = Phaser.Math.RND.integerInRange(1, 4);
		const circle = this.addImage("wallCircle" + i);
		circle.setPosition(x, y - 20);
		// crop out the parts that extend past the wall bounds
		const overflowLeft = Math.max((-this.width / 2 + 180) - (x - circle.width / 2), 0);
		const overflowRight = Math.max((x + circle.width / 2) - (this.width / 2), 0);
		const overflowBottom = Math.max((y + circle.height / 2) - (this.height / 2), 0);
		circle.setCrop(overflowLeft, 0, circle.width - overflowLeft - overflowRight, circle.height - overflowBottom);
	}

	destroyBit(i: number, center: boolean) {
		if (this.bits[i]) {
			this.bits[i] = null;
			this.wallData[i] = center ? 2 : 1;
		}
	}

	getGridPos(globalPos: {x: number, y: number}) {
		const left = this.x - this.width / 2;
		const top = this.y - this.height / 2;
		const indexX = Math.round((globalPos.x - left) / this.bitSize);
		const indexY = Math.round((globalPos.y - top) / this.bitSize);
		return [indexX, indexY];
	}

	getLocalGridPos(globalPos: {x: number, y: number}) {
		const indexX = Math.round((globalPos.x + this.width / 2) / this.bitSize);
		const indexY = Math.round((globalPos.y + this.height / 2) / this.bitSize);
		return [indexX, indexY];
	}

	getLocalPos(globalPos: { x: number, y: number }) {
		return [globalPos.x - this.x, globalPos.y - this.y];
	}

	getGlobalPos(localPos: { x: number, y: number }) {
		return [localPos.x + this.x, localPos.y + this.y];
	}

	getBitIndex(gridX: number, gridY: number): number | null {
		if (gridX < 0 || gridX >= this.xCount) return null;
		const index = gridX + (gridY * this.xCount);
		if (index < 0 || index >= this.bits.length) return null;
		return index;
	}

}

class ParticleEmitter extends GameObject {
	particles: Phaser.GameObjects.Image[] = [];
	lifespan = 3;
	constructor(scene: GameScene) {
		super(scene);

		const count = 5;
		const maxDist = 130;

		for (let i = 0; i < 5; i++) {

			let dist = (i / count) * maxDist;
			let ang = Phaser.Math.RND.realInRange(0, 2 * Math.PI) + (i / count) * 4 * Math.PI;
			const dx = Math.cos(ang);
			const dy = Math.sin(ang);

			const img = new Particle(scene, "wallChunks");
			this.add(img);
			this.particles.push(img);
			img.setPosition(dist * dx, dist * dy);
			img.vx = Phaser.Math.RND.realInRange(0, 800) * dx;
			img.vy = Phaser.Math.RND.realInRange(0, 800) * dy;
		}
	}

	update(dt: number): void {
		super.update(dt);
		for (const particle of this.particles) particle.update(dt);
		this.lifespan -= dt;
		if (this.lifespan < 0) this.destroy();
	}
}

class Particle extends Phaser.GameObjects.Image {
	vx: number;
	vy: number;
	va: number;
	lifespan = 2;

	constructor(scene: GameScene, texture: string) {
		super(scene, 0, 0, texture);
		const frames = this.texture.getFramesFromTextureSource(0, false);
		this.setFrame(Phaser.Math.RND.pick(frames).name);
		this.angle = Phaser.Math.RND.realInRange(0, 360);
		this.va = Math.pow(Phaser.Math.RND.realInRange(0, 1), 2) * 360 * 4;
	}

	update(dt: number): void {
		this.x += this.vx * dt;
		this.y += this.vy * dt;
		this.vy += 800 * dt;
		this.angle += this.va * dt;
		this.lifespan -= dt;
		this.alpha = Phaser.Math.Clamp(this.lifespan, 0, 1);
	}
}